import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import htm from 'htm';
import {
  CONNECTION_LABELS,
  CONTROL_PANEL_MIN_WIDTH,
  CONTROL_PANEL_MAX_WIDTH,
  DEFAULT_CONTROL_PANEL_WIDTH,
} from './controlConstants.js';

const html = htm.bind(React.createElement);

function SearchHelpPopup({ isOpen, onClose }) {
  if (!isOpen) return null;

  return html`
    <div className="as-search-help-backdrop" onClick=${onClose}>
      <div className="as-search-help-popup" onClick=${(e) => e.stopPropagation()}>
        <div className="as-search-help-header">
          <h3>Search Guide</h3>
          <button
            type="button"
            className="as-search-help-close"
            onClick=${onClose}
            aria-label="Close help"
          >
            ×
          </button>
        </div>

        <div className="as-search-help-content">
          <div className="as-search-help-grid">
            <div className="as-search-help-section">
              <h4>Basic</h4>
              <div className="as-search-example">
                <code>john</code>
                <span>Find anywhere</span>
              </div>
              <div className="as-search-example">
                <code>payment failed</code>
                <span>Both terms (AND)</span>
              </div>
            </div>

            <div className="as-search-help-section">
              <h4>Fields</h4>
              <div className="as-search-example">
                <code>user.email:john</code>
                <span>In specific field</span>
              </div>
              <div className="as-search-example">
                <code>price>100</code>
                <span>Numeric compare</span>
              </div>
            </div>

            <div className="as-search-help-section">
              <h4>Exact</h4>
              <div className="as-search-example">
                <code>"exact phrase"</code>
                <span>Exact match</span>
              </div>
              <div className="as-search-example">
                <code>"error: 404"</code>
                <span>With punctuation</span>
              </div>
            </div>

            <div className="as-search-help-section">
              <h4>Exclude</h4>
              <div className="as-search-example">
                <code>-test</code>
                <span>Not "test"</span>
              </div>
              <div className="as-search-example">
                <code>user -bot</code>
                <span>User but not bot</span>
              </div>
            </div>

            <div className="as-search-help-section">
              <h4>Wildcards</h4>
              <div className="as-search-example">
                <code>user*</code>
                <span>Starts with</span>
              </div>
              <div className="as-search-example">
                <code>*@gmail.com</code>
                <span>Ends with</span>
              </div>
              <div className="as-search-example">
                <code>j?hn</code>
                <span>? = any char</span>
              </div>
            </div>

            <div className="as-search-help-section">
              <h4>Advanced</h4>
              <div className="as-search-example">
                <code>type:track event:purchase total>50</code>
                <span>Multiple conditions</span>
              </div>
              <div className="as-search-example">
                <code>"complete" -test email:*@company.com</code>
                <span>Complex query</span>
              </div>
            </div>
          </div>

          <div className="as-search-help-footer">
            <div className="as-search-help-tip">
              <strong>Tips:</strong> Case-insensitive • AND logic • Instant results • Nested paths: <code>user.profile.name</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function ControlPanel({
  onPause,
  onResume,
  connection,
  eventTypeFilter,
  onEventTypeChange,
  search,
  onSearchChange,
  limit,
  onLimitChange,
  canPause,
  canResume,
  panelWidth,
  onPanelWidthChange,
  isCollapsed,
  onCollapse,
  onConnect,
  onDisconnect,
  hasKey,
  onExport,
  eventCount,
  statusMessage,
  statusTone,
  hideConnectionControls = false,
}) {
  const panelWidthValue = Math.round(panelWidth ?? DEFAULT_CONTROL_PANEL_WIDTH);
  const [isResizingControlPanel, setIsResizingControlPanel] = useState(false);
  const [showSearchHelp, setShowSearchHelp] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(panelWidthValue);

  useEffect(() => {
    resizeStartWidth.current = panelWidthValue;
  }, [panelWidthValue]);

  const handleResizeStart = useCallback((event) => {
    if (isCollapsed) {
      return;
    }
    event.preventDefault();
    setIsResizingControlPanel(true);
    resizeStartX.current = event.clientX;
    resizeStartWidth.current = panelWidthValue;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [isCollapsed, panelWidthValue]);

  useEffect(() => {
    if (!isResizingControlPanel) {
      return;
    }

    const handleMouseMove = (event) => {
      const deltaX = event.clientX - resizeStartX.current;
      const proposedWidth = resizeStartWidth.current + deltaX;
      const constrainedWidth = Math.min(
        CONTROL_PANEL_MAX_WIDTH,
        Math.max(CONTROL_PANEL_MIN_WIDTH, proposedWidth)
      );
      onPanelWidthChange?.(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizingControlPanel(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingControlPanel, onPanelWidthChange]);

  useEffect(() => {
    if (!isCollapsed) {
      return;
    }
    if (isResizingControlPanel) {
      setIsResizingControlPanel(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isCollapsed, isResizingControlPanel]);

  if (isCollapsed) {
    return html`
      <section className="as-panel as-panel-collapsed" aria-label="Control Panel">
        <button
          type="button"
          className="as-collapse-toggle"
          onClick=${() => onCollapse(false)}
          aria-label="Expand control panel"
          title="Expand Controls"
        >
          ▶
        </button>
        <div className="as-collapsed-indicator" title="Controls">
          <span style=${{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Controls</span>
        </div>
      </section>
    `;
  }

  return html`
    <${Fragment}>
      <div
        className="as-control-panel-wrapper"
        style=${{
          flexShrink: 0,
          width: `${panelWidthValue}px`,
          position: 'relative',
        }}
      >
        <section className="as-panel" aria-label="Control Panel">
      <header className="as-panel-header">
        <h3>Controls</h3>
        <button
          type="button"
          className="as-collapse-toggle"
          onClick=${() => onCollapse(true)}
          aria-label="Collapse control panel"
        >
          ◀
        </button>
      </header>

      ${!hideConnectionControls ? html`
        <div className="as-control-section">
          <div className="as-section-title">Connection</div>
          <div className="as-connection-status" data-status=${connection}>
            <span className="as-connection-dot"></span>
            <span className="as-connection-label">${CONNECTION_LABELS[connection] ?? CONNECTION_LABELS.disconnected}</span>
          </div>
          ${statusMessage
            ? html`
                <div className=${`as-status-text as-status-text--${statusTone || 'info'}`}>
                  ${statusMessage}
                </div>
              `
            : null}
          <div className="as-connection-actions">
            <button
              type="button"
              className=${`as-action-btn ${connection === 'connected' ? 'as-action-btn--secondary' : 'as-action-btn--primary'}`}
              onClick=${connection === 'disconnected' || connection === 'error'
                ? onConnect
                : connection === 'paused'
                  ? onResume
                  : onDisconnect}
              disabled=${connection === 'connecting' || (!hasKey && (connection === 'disconnected' || connection === 'error'))}
            >
              ${connection === 'connecting'
                ? 'Connecting…'
                : connection === 'paused'
                  ? 'Resume'
                  : connection === 'connected'
                    ? 'Disconnect'
                    : 'Connect'}
            </button>
            <button
              type="button"
              className="as-action-btn as-action-btn--secondary"
              onClick=${onExport}
              disabled=${eventCount === 0}
              title="Export events to JSON"
            >
              Export
            </button>
          </div>
        </div>
      ` : null}

      ${connection === 'connected'
        ? html`
        <div className="as-control-section">
          <div className="as-section-title">Stream Control</div>
          <button
            type="button"
            className="as-action-btn as-action-btn--secondary"
            onClick=${canPause ? onPause : onResume}
            disabled=${!canPause && !canResume}
          >
            ${canPause ? 'Pause' : 'Resume'}
          </button>
          <div className="as-stream-limit">
            <span className="as-limit-label">Display limit</span>
            <div className="as-limit-control">
              <input
                type="range"
                min="1"
                max="1000"
                value=${limit}
                onInput=${onLimitChange}
                className="as-limit-slider"
              />
              <span className="as-limit-value">${limit}</span>
            </div>
          </div>
        </div>

        <div className="as-control-section">
          <div className="as-section-title">
            Filters & Search
            <button
              type="button"
              className="as-search-help"
              onClick=${() => setShowSearchHelp(true)}
              title="View search guide"
            >
              ?
            </button>
          </div>
          <div className="as-search-container">
            <input
              type="text"
              className="as-search-input"
              placeholder='Search: john, user.email:john, "exact phrase", -exclude, user*'
              value=${search}
              onInput=${onSearchChange}
            />
            ${search && html`
              <button
                type="button"
                className="as-search-clear"
                onClick=${() => onSearchChange({ currentTarget: { value: '' } })}
                title="Clear search"
              >
                ×
              </button>
            `}
          </div>
          <div className="as-event-types">
            ${['track', 'page', 'identify', 'group', 'alias', 'screen'].map((type) => html`
              <button
                type="button"
                key=${type}
                className=${`as-type-chip ${eventTypeFilter.includes(type) ? 'active' : ''}`}
                onClick=${() => onEventTypeChange(type)}
              >
                ${type}
              </button>
            `)}
          </div>
        </div>
      `
        : null}
        </section>
        <div
          className="as-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize control panel"
          onMouseDown=${handleResizeStart}
          onMouseEnter=${(event) => {
            event.currentTarget.style.background = 'rgba(88, 166, 255, 0.1)';
          }}
          onMouseLeave=${(event) => {
            event.currentTarget.style.background = 'transparent';
          }}
          style=${{
            position: 'absolute',
            right: '-5px',
            top: 0,
            bottom: 0,
            width: '10px',
            cursor: 'ew-resize',
            zIndex: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            transition: 'background 0.2s ease',
          }}
        >
          <div
            style=${{
              width: '3px',
              height: '40px',
              background: isResizingControlPanel ? 'var(--as-accent)' : 'var(--as-border)',
              borderRadius: '2px',
              opacity: isResizingControlPanel ? 1 : 0.5,
              transition: 'all 0.2s ease',
            }}
          />
        </div>
      </div>
      <${SearchHelpPopup} isOpen=${showSearchHelp} onClose=${() => setShowSearchHelp(false)} />
    </${Fragment}>
  `;
}

export default ControlPanel;
